import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import {
    CREATE_SLIDES,
    CREATE_SLIDE_FILE,
    CREATE_SLIDE_IMAGE,
    CreateSlideFileResponse,
    CreateSlideFileVariables,
    CreateSlideImageResponse,
    CreateSlideImageVariables,
    CreateSlidesResponse,
    CreateSlidesVariables,
    DELETE_SLIDE,
    DeleteSlideResponse,
    DeleteSlideVariables,
    GET_SLIDE_FILES,
    GET_SLIDE_IMAGES,
    GetSlideFilesResponse,
    GetSlideImagesResponse,
    UPDATE_SLIDES,
    UpdateSlidesResponse,
    UpdateSlidesVariables,
} from './gql';
import { useCallback } from 'react';
import { Slide as SlideData } from '@/types/slide';

export const useSlides = () => {
    const client = useApolloClient();

    // gql
    const [querySlideFiles] = useLazyQuery<GetSlideFilesResponse>(GET_SLIDE_FILES);
    const [querySlideImages] = useLazyQuery<GetSlideImagesResponse>(GET_SLIDE_IMAGES);
    const [updateSlides] = useMutation<UpdateSlidesResponse, UpdateSlidesVariables>(UPDATE_SLIDES);
    const [createSlides] = useMutation<CreateSlidesResponse, CreateSlidesVariables>(CREATE_SLIDES);
    const [deleteSlide] = useMutation<DeleteSlideResponse, DeleteSlideVariables>(DELETE_SLIDE);
    const [createSlideImage] = useMutation<CreateSlideImageResponse, CreateSlideImageVariables>(CREATE_SLIDE_IMAGE);
    const [createSlideFile] = useMutation<CreateSlideFileResponse, CreateSlideFileVariables>(CREATE_SLIDE_FILE);

    const invalidateCache = useCallback(() => {
        /**
         * Don't bother to update the cache for slides, as the way we structure the data, there is a lot of side effects
         * that can happen when updating slides, such as changing the order of slides, adding or removing slides, last updated lesson timestamp etc.
         * So we just evict the cache for lessons and slides, and let the components refetch the data.
         */
        client.cache.evict({ fieldName: 'lessons' });
        client.cache.evict({ fieldName: 'slides' });
    }, [client]);

    const update = useCallback(
        async (variables: UpdateSlidesVariables) => {
            try {
                await updateSlides({ variables });
                invalidateCache();
            } catch (error) {
                return Promise.reject(new Error(`Failed to update slides: ${error}`));
            }
        },
        [invalidateCache, updateSlides],
    );

    const add = useCallback(
        async (lessonVersionID: string, data: string, fileIds: string[]): Promise<SlideData[] | undefined> => {
            try {
                const res = await createSlides({
                    variables: {
                        data: [
                            {
                                lessonVersion: { connect: { id: lessonVersionID } },
                                data: data,
                                ...(fileIds.length > 0 && {
                                    files: {
                                        connect: fileIds.map((fileId) => ({ id: fileId })),
                                    },
                                }),
                            },
                        ],
                    },
                });
                invalidateCache();
                return res.data?.createSlides;
            } catch (error) {
                return Promise.reject(new Error(`Failed to create slide: ${error}`));
            }
        },
        [createSlides, invalidateCache],
    );

    const remove = useCallback(
        async (slideID: string) => {
            try {
                const res = await deleteSlide({ variables: { slideID: slideID } });
                invalidateCache();
                return res.data?.deleteSlide;
            } catch (error) {
                return Promise.reject(new Error(`Failed to delete slide: ${error}`));
            }
        },
        [deleteSlide, invalidateCache],
    );

    const uploadImage = useCallback(
        async (slideId: string, file: File) => {
            const res = await createSlideImage({ variables: { slideId: slideId, file: file } });
            return res.data?.createSlideImage;
        },
        [createSlideImage],
    );

    const uploadFile = useCallback(
        async (slideId: string, file: File) => {
            const res = await createSlideFile({
                variables: {
                    data: {
                        file: { upload: file },
                        ...(slideId && {
                            slides: {
                                connect: [
                                    {
                                        id: slideId,
                                    },
                                ],
                            },
                        }),
                    },
                },
            });
            return res.data?.createSlideFile;
        },
        [createSlideFile],
    );

    const getSlideFiles = useCallback(
        async (slideId: string) => {
            const result = await querySlideFiles({
                variables: {
                    where: {
                        slides: {
                            some: {
                                id: {
                                    equals: slideId,
                                },
                            },
                        },
                    },
                },
                fetchPolicy: 'network-only',
            });
            return result.data?.slideFiles || [];
        },
        [querySlideFiles],
    );

    const getSlideImages = useCallback(
        async (slideId: string) => {
            const result = await querySlideImages({
                variables: {
                    where: {
                        slides: {
                            some: {
                                id: {
                                    equals: slideId,
                                },
                            },
                        },
                    },
                },
                fetchPolicy: 'network-only',
            });
            return result.data?.slideImages || [];
        },
        [querySlideImages],
    );

    const duplicate = useCallback(
        async (variables: CreateSlidesVariables['data']): Promise<SlideData[] | undefined> => {
            try {
                const res = await createSlides({
                    variables: {
                        data: variables,
                    },
                });
                invalidateCache();
                return res.data?.createSlides;
            } catch (error) {
                return Promise.reject(new Error(`Failed to create slide: ${error}`));
            }
        },
        [createSlides, invalidateCache],
    );

    return {
        update,
        add,
        remove,
        uploadImage,
        uploadFile,
        getSlideFiles,
        getSlideImages,
        duplicate,
    };
};
